import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { CheckboxProps } from './Checkbox.types';
import { TooltipIcon } from '../TooltipIcon';

// https://appitventures.com/blog/styling-checkbox-css-tips/
const Container = styled.div`
  display: flex;
  flex-direction: row;

  label {
    cursor: pointer;
  }

  > :first-child {
    padding-right: 0.5em;
  }

  .checkbox-label {
    display: block;
    position: relative;
    cursor: pointer;
    font-size: 22px;
    line-height: 20px;
    min-height: 20px;
    min-width: 20px;
    clear: both;
  }

  .checkbox-label input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .checkbox-label .checkbox {
    position: absolute;
    top: 0px;
    left: 0px;
    height: 20px;
    width: 20px;
    background-color: white;
    border-radius: 5px;
    transition: all 0.2s ease-out;
    border: 1px solid lightgray;
  }

  .checkbox-label input:checked ~ .checkbox {
    background-color: ${props =>
      props.theme.component.checkbox.checked.backgroundColor};
    border-radius: 5px;
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }

  .checkbox-label input:disabled ~ .checkbox {
    background-color: ${props => props.theme.colors.disabledColor};
    cursor: not-allowed;
  }

  .checkbox-label .checkbox::after {
    position: absolute;
    content: '';
    left: 10px;
    top: 10px;
    height: 0px;
    width: 0px;
    border-radius: 5px;
    border: solid ${props => props.theme.colors.primaryColor};
    border-width: 0 3px 3px 0;
    transform: rotate(0deg) scale(0);
    opacity: 1;
    transition: all 0.1s ease-out;
  }

  .checkbox-label input:checked ~ .checkbox::after {
    transform: rotate(45deg) scale(1);
    opacity: 1;
    left: 6px;
    top: 3px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    background-color: transparent;
    border-radius: 0;
  }

  &.secondary {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0 19px;
    gap: 8px;
    height: 54px;
    background: #ffffff;
    border: 1px solid #dddfe4;
    cursor: pointer;
    label:not(.checkbox-label) {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
    }
    &:hover {
      background: rgba(247, 233, 252, 0.5);
    }
    .checkbox-label input:checked ~ .checkbox {
      background: #b93be4;
      border-radius: 1px;
      border: none;
    }
    .checkbox-label input[type='radio'] ~ .checkbox {
      border-radius: 50% !important;
    }
    .checkbox-label input[type='radio']:checked ~ .checkbox::after {
      border: 2px solid white;
      border-radius: 50%;
      width: 80%;
      height: 80%;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
    }
    .checkbox-label input[type='radio']:checked ~ .checkbox {
      border: 2px solid #dddfe4;
    }
  }
`;

export const Checkbox: FunctionComponent<CheckboxProps> = props => {
  const { id, label, style, tooltip, variant, ...rest } = props;

  return (
    <Container style={style} className={variant}>
      <label className="checkbox-label">
        <input id={id} data-testid={id} {...rest} />
        <span className="checkbox" />
      </label>
      {label && (
        <label htmlFor={id}>
          {label}
          {tooltip && (
            <>
              {' '}
              <TooltipIcon tooltip={tooltip} />
            </>
          )}
        </label>
      )}
    </Container>
  );
};
